/**
 * AppMapViewer - Mapare vizuală a relațiilor dintre entitățile aplicației
 * Sistem de coloane ramificat care arată legăturile între module
 */
import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { 
  Building2, Users, FileText, Wrench, Car, Receipt, FolderKanban,
  MapPin, ChevronRight, ChevronDown, Link2, ArrowRight, Layers,
  FileSignature, Calculator, Package, Clock, UserCheck, Shield,
  Banknote, BarChart3, Settings, Database, GitBranch
} from "lucide-react";

// Structura completă a aplicației cu relațiile între entități
const APP_STRUCTURE = {
  id: 'firma',
  name: 'Firma (Companie)',
  icon: Building2,
  color: 'bg-purple-500',
  description: 'Entitatea principală - compania care folosește aplicația',
  fields: ['nume', 'CUI', 'adresă', 'IBAN', 'reprezentant'],
  children: [
    {
      id: 'angajati',
      name: 'Angajați',
      icon: Users,
      color: 'bg-blue-500',
      description: 'Toți angajații firmei',
      fields: ['nume', 'email', 'telefon', 'funcție', 'salariu'],
      relation: 'Firma are mai mulți Angajați',
      children: [
        {
          id: 'pontaj',
          name: 'Pontaj',
          icon: Clock,
          color: 'bg-cyan-500',
          description: 'Înregistrările de prezență',
          fields: ['dată', 'ore lucrate', 'tip (prezent/absent/CO)'],
          relation: 'Angajatul are înregistrări Pontaj'
        },
        {
          id: 'concedii',
          name: 'Cereri Concediu',
          icon: FileText,
          color: 'bg-green-500',
          description: 'Cererile de concediu ale angajaților',
          fields: ['tip', 'dată început', 'dată sfârșit', 'status'],
          relation: 'Angajatul depune Cereri de Concediu'
        },
        {
          id: 'fise_ssm',
          name: 'Fișe SSM/PSI',
          icon: Shield,
          color: 'bg-amber-500',
          description: 'Documentele de securitate în muncă',
          fields: ['tip', 'dată', 'semnătură'],
          relation: 'Angajatul semnează Fișe SSM'
        }
      ]
    },
    {
      id: 'proiecte',
      name: 'Proiecte (Șantiere)',
      icon: MapPin,
      color: 'bg-orange-500',
      description: 'Șantierele/locațiile de lucru',
      fields: ['nume', 'adresă', 'dată început', 'dată sfârșit', 'buget'],
      relation: 'Firma gestionează mai multe Proiecte',
      children: [
        {
          id: 'faze_proiect',
          name: 'Faze Proiect',
          icon: Layers,
          color: 'bg-pink-500',
          description: 'Etapele de execuție ale proiectului',
          fields: ['nume', 'dată început', 'dată sfârșit', 'cheltuieli'],
          relation: 'Proiectul are mai multe Faze',
          children: [
            {
              id: 'cheltuieli_faza',
              name: 'Cheltuieli Fază',
              icon: Banknote,
              color: 'bg-red-400',
              description: 'Cheltuielile asociate fazei',
              fields: ['descriere', 'sumă', 'dată'],
              relation: 'Faza are Cheltuieli'
            }
          ]
        },
        {
          id: 'contracte_proiect',
          name: 'Contracte',
          icon: FileSignature,
          color: 'bg-indigo-500',
          description: 'Contractele de prestări servicii',
          fields: ['nr. contract', 'beneficiar', 'valoare', 'termen'],
          relation: 'Proiectul are Contract asociat',
          children: [
            {
              id: 'devize',
              name: 'Devize',
              icon: Calculator,
              color: 'bg-teal-500',
              description: 'Devizele de lucrări',
              fields: ['nr. deviz', 'categorii', 'materiale', 'manoperă'],
              relation: 'Contractul are Devize anexate',
              children: [
                {
                  id: 'facturi_deviz',
                  name: 'Facturi',
                  icon: Receipt,
                  color: 'bg-emerald-500',
                  description: 'Facturile generate din deviz',
                  fields: ['serie', 'număr', 'client', 'valoare', 'status'],
                  relation: 'Din Deviz se generează Facturi'
                }
              ]
            }
          ]
        },
        {
          id: 'echipa_proiect',
          name: 'Echipă Proiect',
          icon: UserCheck,
          color: 'bg-blue-400',
          description: 'Angajații alocați proiectului',
          fields: ['angajat', 'rol', 'dată alocare'],
          relation: 'Proiectul are Echipă de angajați',
          linkedTo: 'angajati'
        },
        {
          id: 'unelte_proiect',
          name: 'Unelte Alocate',
          icon: Wrench,
          color: 'bg-slate-500',
          description: 'Uneltele folosite pe proiect',
          fields: ['unealtă', 'ore utilizare', 'dată'],
          relation: 'Proiectul folosește Unelte',
          linkedTo: 'unelte'
        }
      ]
    },
    {
      id: 'unelte',
      name: 'Unelte',
      icon: Wrench,
      color: 'bg-slate-600',
      description: 'Inventarul de unelte și echipamente',
      fields: ['nume', 'tip', 'serie', 'stare', 'locație'],
      relation: 'Firma deține Unelte',
      children: [
        {
          id: 'utilizare_unelte',
          name: 'Istoric Utilizare',
          icon: Clock,
          color: 'bg-slate-400',
          description: 'Înregistrările de utilizare',
          fields: ['dată', 'ore', 'angajat', 'proiect'],
          relation: 'Unealta are Istoric de utilizare'
        }
      ]
    },
    {
      id: 'vehicule',
      name: 'Vehicule',
      icon: Car,
      color: 'bg-sky-600',
      description: 'Parcul auto al firmei',
      fields: ['marcă', 'nr. înmatriculare', 'an', 'km'],
      relation: 'Firma deține Vehicule',
      children: [
        {
          id: 'foi_parcurs',
          name: 'Foi de Parcurs',
          icon: FileText,
          color: 'bg-sky-400',
          description: 'Documentele de transport',
          fields: ['dată', 'șofer', 'km parcurși', 'combustibil'],
          relation: 'Vehiculul are Foi de parcurs'
        }
      ]
    },
    {
      id: 'inventar',
      name: 'Inventar Materiale',
      icon: Package,
      color: 'bg-amber-600',
      description: 'Stocul de materiale',
      fields: ['denumire', 'cantitate', 'preț', 'furnizor'],
      relation: 'Firma are Inventar de materiale',
      children: [
        {
          id: 'intrari_inventar',
          name: 'Intrări Stoc',
          icon: ArrowRight,
          color: 'bg-green-400',
          description: 'Aprovizionări materiale',
          fields: ['dată', 'cantitate', 'factură proveniență'],
          relation: 'Inventarul are Intrări'
        },
        {
          id: 'iesiri_inventar',
          name: 'Ieșiri Stoc',
          icon: ArrowRight,
          color: 'bg-red-400',
          description: 'Consumuri materiale',
          fields: ['dată', 'cantitate', 'proiect destinație'],
          relation: 'Inventarul are Ieșiri'
        }
      ]
    },
    {
      id: 'facturare',
      name: 'Facturare',
      icon: Receipt,
      color: 'bg-emerald-600',
      description: 'Sistemul de facturare',
      fields: ['setări serie', 'TVA', 'date firmă'],
      relation: 'Firma are modul de Facturare',
      children: [
        {
          id: 'facturi_emise',
          name: 'Facturi Emise',
          icon: Receipt,
          color: 'bg-emerald-500',
          description: 'Facturile către clienți',
          fields: ['serie', 'număr', 'client', 'valoare', 'status'],
          relation: 'Se emit Facturi către clienți'
        },
        {
          id: 'facturi_primite',
          name: 'Facturi Primite',
          icon: Receipt,
          color: 'bg-emerald-400',
          description: 'Facturile de la furnizori',
          fields: ['furnizor', 'număr', 'valoare', 'dată scadență'],
          relation: 'Se primesc Facturi de la furnizori'
        },
        {
          id: 'clienti',
          name: 'Clienți',
          icon: Users,
          color: 'bg-violet-500',
          description: 'Baza de date clienți',
          fields: ['denumire', 'CUI', 'adresă', 'contact'],
          relation: 'Facturare are Listă de Clienți'
        }
      ]
    },
    {
      id: 'documente',
      name: 'Documente',
      icon: FolderKanban,
      color: 'bg-violet-600',
      description: 'Arhiva de documente',
      fields: ['tip', 'titlu', 'dată'],
      relation: 'Firma are Arhivă documente',
      children: [
        {
          id: 'contracte_munca',
          name: 'Contracte Muncă',
          icon: FileSignature,
          color: 'bg-violet-400',
          description: 'Contractele de muncă',
          fields: ['angajat', 'dată', 'tip'],
          relation: 'Se arhivează Contracte de muncă'
        },
        {
          id: 'contracte_servicii',
          name: 'Contracte Servicii',
          icon: FileSignature,
          color: 'bg-indigo-400',
          description: 'Contractele cu clienții',
          fields: ['client', 'valoare', 'termen'],
          relation: 'Se arhivează Contracte de servicii'
        }
      ]
    }
  ]
};

// Componenta pentru un nod din arbore
function TreeNode({ node, level = 0, expandedNodes, toggleNode, selectedNode, setSelectedNode }) {
  const isExpanded = expandedNodes.includes(node.id);
  const hasChildren = node.children && node.children.length > 0;
  const isSelected = selectedNode?.id === node.id;
  const Icon = node.icon;
  
  return (
    <div className="relative">
      {/* Linia verticală de conectare */}
      {level > 0 && (
        <div 
          className="absolute left-0 top-0 bottom-0 w-px bg-slate-300" 
          style={{ marginLeft: `${(level - 1) * 24 + 12}px` }}
        />
      )}
      
      <div
        className={`
          flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-all
          ${isSelected ? 'bg-slate-100 ring-2 ring-slate-300' : 'hover:bg-slate-50'}
        `}
        style={{ marginLeft: `${level * 24}px` }}
        onClick={() => setSelectedNode(node)}
      >
        {/* Buton expand/collapse */}
        {hasChildren ? (
          <button
            onClick={(e) => {
              e.stopPropagation();
              toggleNode(node.id);
            }}
            className="w-5 h-5 flex items-center justify-center rounded hover:bg-slate-200"
          >
            {isExpanded ? (
              <ChevronDown className="w-4 h-4 text-slate-500" />
            ) : (
              <ChevronRight className="w-4 h-4 text-slate-500" />
            )}
          </button>
        ) : (
          <div className="w-5" />
        )}
        
        {/* Icon și nume */}
        <div className={`p-1.5 rounded ${node.color} text-white`}>
          <Icon className="w-4 h-4" />
        </div>
        <span className="font-medium text-sm">{node.name}</span>
        
        {/* Badge cu număr de copii */}
        {hasChildren && (
          <Badge variant="outline" className="ml-auto text-xs">
            {node.children.length}
          </Badge>
        )}
        
        {/* Indicator de legătură */}
        {node.linkedTo && (
          <Link2 className="w-3 h-3 text-slate-400 ml-1" title={`Legat de: ${node.linkedTo}`} />
        )}
      </div>
      
      {/* Copii */}
      {isExpanded && hasChildren && (
        <div className="mt-1">
          {node.children.map((child) => (
            <TreeNode
              key={child.id}
              node={child}
              level={level + 1}
              expandedNodes={expandedNodes}
              toggleNode={toggleNode}
              selectedNode={selectedNode}
              setSelectedNode={setSelectedNode}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// Componenta pentru detaliile nodului selectat
function NodeDetails({ node }) {
  if (!node) return null;
  
  const Icon = node.icon;
  
  return (
    <Card className="border-0 shadow-lg">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${node.color} text-white`}>
            <Icon className="w-5 h-5" />
          </div>
          <div>
            <h3 className="text-lg font-bold">{node.name}</h3>
            <p className="text-sm text-slate-500 font-normal">{node.description}</p>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Relația */}
        {node.relation && (
          <div className="p-3 bg-slate-50 rounded-lg">
            <div className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-1">
              <GitBranch className="w-4 h-4" />
              Relație
            </div>
            <p className="text-sm text-slate-600">{node.relation}</p>
          </div>
        )}
        
        {/* Câmpuri */}
        {node.fields && node.fields.length > 0 && (
          <div>
            <div className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-2">
              <Database className="w-4 h-4" />
              Câmpuri principale
            </div>
            <div className="flex flex-wrap gap-2">
              {node.fields.map((field, idx) => (
                <Badge key={field} variant="secondary" className="text-xs">
                  {field}
                </Badge>
              ))}
            </div>
          </div>
        )}
        
        {/* Legături */}
        {node.linkedTo && (
          <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex items-center gap-2 text-sm font-medium text-blue-700">
              <Link2 className="w-4 h-4" />
              Legătură cu: <span className="font-bold">{node.linkedTo}</span>
            </div>
          </div>
        )}
        
        {/* Copii */}
        {node.children && node.children.length > 0 && (
          <div>
            <div className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-2">
              <Layers className="w-4 h-4" />
              Entități conectate ({node.children.length})
            </div>
            <div className="grid grid-cols-2 gap-2">
              {node.children.map((child) => {
                const ChildIcon = child.icon;
                return (
                  <div 
                    key={child.id} 
                    className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg"
                  >
                    <div className={`p-1 rounded ${child.color} text-white`}>
                      <ChildIcon className="w-3 h-3" />
                    </div>
                    <span className="text-xs font-medium">{child.name}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// Componenta pentru vizualizarea fluxului
function FlowDiagram() {
  const flows = [
    {
      title: 'Flux Facturare',
      color: 'emerald',
      steps: ['Proiect', 'Contract', 'Deviz', 'Factură', 'Încasare']
    },
    {
      title: 'Flux Angajați',
      color: 'blue',
      steps: ['Angajare', 'Contract Muncă', 'Alocare Proiect', 'Pontaj', 'Salariu']
    },
    {
      title: 'Flux Materiale',
      color: 'amber',
      steps: ['Comandă', 'Recepție', 'Stoc', 'Alocare Proiect', 'Consum']
    }
  ];
  
  return (
    <Card className="border-0 shadow-lg">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <BarChart3 className="w-4 h-4" />
          Fluxuri Principale
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {flows.map((flow, idx) => (
          <div key={flow.title} className="space-y-2">
            <p className={`text-xs font-semibold text-${flow.color}-600`}>{flow.title}</p>
            <div className="flex items-center gap-1 flex-wrap">
              {flow.steps.map((step, stepIdx) => (
                <div key={step} className="flex items-center">
                  <Badge 
                    variant="outline" 
                    className={`text-xs border-${flow.color}-300 text-${flow.color}-700`}
                  >
                    {step}
                  </Badge>
                  {stepIdx < flow.steps.length - 1 && (
                    <ArrowRight className={`w-3 h-3 mx-1 text-${flow.color}-400`} />
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

export default function AppMapViewer() {
  const [expandedNodes, setExpandedNodes] = useState(['firma']);
  const [selectedNode, setSelectedNode] = useState(APP_STRUCTURE);
  
  const toggleNode = (nodeId) => {
    setExpandedNodes(prev => 
      prev.includes(nodeId) 
        ? prev.filter(id => id !== nodeId)
        : [...prev, nodeId]
    );
  };
  
  const expandAll = () => {
    const getAllNodeIds = (node) => {
      let ids = [node.id];
      if (node.children) {
        node.children.forEach(child => {
          ids = [...ids, ...getAllNodeIds(child)];
        });
      }
      return ids;
    };
    setExpandedNodes(getAllNodeIds(APP_STRUCTURE));
  };
  
  const collapseAll = () => {
    setExpandedNodes(['firma']);
  };
  
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold flex items-center gap-2">
            <GitBranch className="w-6 h-6 text-indigo-600" />
            Mapare Aplicație
          </h2>
          <p className="text-sm text-slate-500">
            Structura și relațiile dintre entitățile aplicației
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={expandAll}>
            Expandează Tot
          </Button>
          <Button variant="outline" size="sm" onClick={collapseAll}>
            Restrânge
          </Button>
        </div>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Arborele de entități */}
        <Card className="lg:col-span-2 border-0 shadow-lg">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Database className="w-4 h-4" />
              Structura Entităților
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[500px] pr-4">
              <TreeNode
                node={APP_STRUCTURE}
                expandedNodes={expandedNodes}
                toggleNode={toggleNode}
                selectedNode={selectedNode}
                setSelectedNode={setSelectedNode}
              />
            </ScrollArea>
          </CardContent>
        </Card>
        
        {/* Detalii și fluxuri */}
        <div className="space-y-4">
          <NodeDetails node={selectedNode} />
          <FlowDiagram />
        </div>
      </div>
      
      {/* Legendă */}
      <Card className="border-0 shadow-lg bg-gradient-to-r from-slate-50 to-slate-100">
        <CardContent className="pt-4">
          <div className="flex items-center gap-2 text-sm font-medium text-slate-700 mb-3">
            <Settings className="w-4 h-4" />
            Legendă Culori Module
          </div>
          <div className="flex flex-wrap gap-3">
            {[
              { color: 'bg-purple-500', name: 'Firma' },
              { color: 'bg-blue-500', name: 'Personal' },
              { color: 'bg-orange-500', name: 'Proiecte' },
              { color: 'bg-emerald-500', name: 'Facturare' },
              { color: 'bg-amber-600', name: 'Inventar' },
              { color: 'bg-slate-600', name: 'Unelte' },
              { color: 'bg-sky-600', name: 'Vehicule' },
              { color: 'bg-violet-600', name: 'Documente' },
              { color: 'bg-indigo-500', name: 'Contracte' }
            ].map((item, idx) => (
              <div key={item.name} className="flex items-center gap-2">
                <div className={`w-3 h-3 rounded ${item.color}`} />
                <span className="text-xs text-slate-600">{item.name}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
